import { useState } from 'react';
import { MessageSquare, Mail, Loader2, Send } from 'lucide-react';
import { Button, useToast } from '@afrilink/ui';
import { salesService, type LeadRow, type LeadOrder } from '@/domains/sales/services/salesService';
import { introMailtoHref } from '../lib/introEmail';

/**
 * Per-row actions for the leads lists (SalesLeadsPage + SalesMyLeadsPage).
 * "Resend invoice" shows only while the lead is pre-conversion + unpaid
 * (Option C, 2026-07-09) — the sales-invoice-resend EF rejects paid/converted
 * regardless. "Note" always. Shared so the two lists never drift.
 */
export function LeadRowActions({
  row,
  order,
  onNote,
}: {
  row: LeadRow;
  order: LeadOrder | undefined;
  onNote: () => void;
}) {
  const { toast } = useToast();
  const [resending, setResending] = useState(false);

  const canResend =
    !!order &&
    !!order.invoice_number &&
    (order.payment_state === 'pending_payment' ||
      order.payment_state === 'unpaid' ||
      order.payment_state === 'draft');

  async function onResend() {
    setResending(true);
    try {
      const { to_email } = await salesService.resendLeadInvoice(row.id);
      toast({ tone: 'success', title: 'Invoice re-sent', body: `Pay-link emailed to ${to_email}.` });
    } catch (e) {
      toast({ tone: 'danger', title: 'Could not resend invoice', body: (e as Error).message });
    } finally {
      setResending(false);
    }
  }

  // Intro email is a first-touch action — hide it once the lead has converted.
  const canIntro = row.sales_stage !== 'customer';

  return (
    <div className="flex items-center justify-end gap-1">
      {canIntro && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => { window.location.href = introMailtoHref(row, 'warm'); }}
          aria-label={`Email intro to ${row.full_name}`}
          title="Open a pre-filled intro email in your mail app"
        >
          <Send className="h-4 w-4" /> Intro
        </Button>
      )}
      {canResend && (
        <Button
          size="sm"
          variant="ghost"
          onClick={onResend}
          disabled={resending}
          aria-label={`Resend invoice for ${row.full_name}`}
        >
          {resending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Resend
        </Button>
      )}
      <Button size="sm" variant="ghost" onClick={onNote} aria-label={`Add note for ${row.full_name}`}>
        <MessageSquare className="h-4 w-4" /> Note
      </Button>
    </div>
  );
}
